import { useEffect } from 'react';

const SITE = 'https://usetally.ca';
const OG_IMAGE = `${SITE}/og-image.png`;

const ORG = {
  '@type': 'Organization',
  name: 'Tally',
  url: SITE,
  logo: `${SITE}/logo.png`,
};

function breadcrumb(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

/**
 * SEO configuration for each page. Optional `jsonld` (object or array) is
 * injected as a managed <script id="route-jsonld"> and cleared on route change.
 */
const SEO_CONFIG = {
  '/': {
    // '/' serves the marketing landing to logged-out visitors & crawlers.
    title: 'Tally | Canadian Budgeting App - Track Spending, Bills & Save Money',
    description: "Tally is Canada's simple budgeting app. Connect your bank, track spending, manage bills, and reach your financial goals. Free for students & seniors. Start your 7-day free trial.",
    canonical: `${SITE}/`,
    // Homepage JSON-LD lives statically in index.html — don't duplicate here.
  },
  '/welcome': {
    title: 'Tally | Canadian Budgeting App - Track Spending & Save Money',
    description: "Canada's simple budgeting app. Connect your bank, track spending, manage bills, and save money. Free for students & seniors.",
    canonical: `${SITE}/`,
  },
  '/pricing': {
    title: 'Pricing | Tally - Affordable Budgeting for Canadians',
    description: 'Free for students under 25 and seniors 65+. Tally Power is just $14.99/month with a 7-day free trial. Simple, transparent pricing in CAD — no hidden fees.',
    jsonld: [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Tally',
        applicationCategory: 'FinanceApplication',
        operatingSystem: 'Web',
        url: `${SITE}/pricing`,
        image: OG_IMAGE,
        offers: [
          { '@type': 'Offer', name: 'Tally Student', price: '0', priceCurrency: 'CAD', description: 'Free for users under 25' },
          { '@type': 'Offer', name: 'Tally Power', price: '14.99', priceCurrency: 'CAD', description: 'Full access with 7-day free trial' },
          { '@type': 'Offer', name: 'Tally Senior', price: '0', priceCurrency: 'CAD', description: 'Free for users 65+' },
        ],
        aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', ratingCount: '150' },
      },
      breadcrumb([
        { name: 'Home', url: `${SITE}/` },
        { name: 'Pricing', url: `${SITE}/pricing` },
      ]),
    ],
  },
  '/login': {
    title: 'Log In | Tally',
    description: 'Log in to your Tally account to manage your finances, track spending, and stay on budget.',
  },
  '/signup': {
    title: 'Sign Up | Tally - Start Your Free Trial',
    description: 'Create your Tally account and start your 7-day free trial. Connect your bank in minutes and take control of your finances.',
  },
  '/articles': {
    title: 'Budgeting Tips & Guides for Canadians | Tally Blog',
    description: 'Practical guides on budgeting, tracking bills, saving money, and building wealth — written for Canadians, by Tally.',
    jsonld: [
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'Tally Blog',
        description: 'Practical personal-finance guides for Canadians: budgeting, bills, saving, and building wealth.',
        url: `${SITE}/articles`,
        publisher: ORG,
      },
      breadcrumb([
        { name: 'Home', url: `${SITE}/` },
        { name: 'Articles', url: `${SITE}/articles` },
      ]),
    ],
  },
  '/affiliates': {
    title: 'Earn Money Promoting Tally | Affiliate Program',
    description: 'Join the Tally affiliate program. Earn $25-$80 CAD per referral. Simple tracking, transparent payouts.',
  },
  '/affiliates/login': { title: 'Affiliate Login | Tally', description: 'Access your Tally affiliate dashboard.' },
  '/affiliates/dashboard': { title: 'Affiliate Dashboard | Tally', description: 'Track your affiliate performance, conversions, and earnings.' },
  '/advisor/login': { title: 'Advisor Login | Tally', description: 'Access the Tally advisor portal to collaborate with clients.' },
  '/advisor/dashboard': { title: 'Advisor Portal | Tally', description: 'Manage your clients, view snapshots, and collaborate securely.' },
  '/demo': { title: 'Try Tally — Interactive Demo | See Your Money Clearly', description: 'Try Tally in 30 seconds. See what your spending really looks like — no signup required.' },
  '/transactions': { title: 'Transactions | Tally', description: 'View all your transactions in one place. Search, filter, and categorize your spending.' },
  '/budget': { title: 'Budget | Tally', description: 'Create and manage budgets by category. Track your spending and stay on target.' },
  '/bills': { title: 'Bills | Tally', description: 'Track recurring bills and never miss a payment. Sync with your calendar for reminders.' },
  '/plan': { title: 'Monthly Plan | Tally', description: 'See your month-end snapshot with income, expenses, and savings rate at a glance.' },
  '/insights': { title: 'Spending Insights | Tally', description: 'Get AI-powered insights into your spending habits and personalized recommendations.' },
  '/goals': { title: 'Financial Goals | Tally', description: 'Set and track SMART financial goals. Save for what matters most to you.' },
  '/net-worth': { title: 'Net Worth | Tally', description: 'Track your net worth over time. See your assets, liabilities, and progress.' },
  '/courses': { title: 'Financial Courses | Tally', description: 'Learn personal finance with free courses. Budgeting, saving, investing, and more.' },
  '/cash-flow': { title: 'Cash Flow | Tally', description: '90-day cash flow forecast. Plan ahead and avoid shortfalls.' },
  '/settings': { title: 'Settings | Tally', description: 'Manage your account settings, subscription, and preferences.' },
  '/privacy': { title: 'Privacy Policy | Tally', description: 'Learn how Tally protects your data. PIPEDA-compliant privacy practices for Canadian users.' },
  '/terms': { title: 'Terms of Service | Tally', description: 'Tally Terms of Service. Read our terms and conditions for using the app.' },
  '/docs': { title: 'Help & Documentation | Tally', description: 'Complete user manual for Tally. Learn how to use all features including budgeting, bills, AI insights, and more.' },
};

// ---- DOM helpers ----
function upsertCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setMeta(selector, content) {
  const el = document.querySelector(selector);
  if (el && content != null) el.setAttribute('content', content);
}

function setRouteJsonLd(data) {
  let el = document.getElementById('route-jsonld');
  if (data == null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'route-jsonld';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function cleanPath(path) {
  if (!path || path === '/') return `${SITE}/`;
  return SITE + path.replace(/\/+$/, '');
}

function applySEO(config, path) {
  const canonical = (config && config.canonical) || cleanPath(path);
  upsertCanonical(canonical);
  setMeta('meta[property="og:url"]', canonical);
  setMeta('meta[name="twitter:url"]', canonical);

  if (!config) {
    // Unknown/dynamic route (e.g. an article) — canonical only; the page
    // component sets its own title/description/JSON-LD.
    return;
  }
  document.title = config.title;
  setMeta('meta[name="description"]', config.description);
  setMeta('meta[property="og:title"]', config.ogTitle || config.title);
  setMeta('meta[property="og:description"]', config.description);
  setMeta('meta[name="twitter:title"]', config.ogTitle || config.title);
  setMeta('meta[name="twitter:description"]', config.description);
  setMeta('meta[property="og:image"]', config.ogImage || OG_IMAGE);
  setMeta('meta[name="twitter:image"]', config.ogImage || OG_IMAGE);
  setRouteJsonLd(config.jsonld || null);
}

/**
 * Hook to set title, description, canonical, OG/Twitter and JSON-LD per route.
 */
export const useSEO = (path) => {
  useEffect(() => {
    applySEO(SEO_CONFIG[path], path);
  }, [path]);
};

/**
 * Dynamic SEO for a single article/blog page. Call from the article page once
 * its data has loaded. Sets title, description, canonical, OG and Article JSON-LD.
 */
export const useArticleSEO = (article) => {
  useEffect(() => {
    if (!article) return;
    const slug = article.slug || '';
    const url = `${SITE}/articles/${slug}`;
    const title = article.seo_title || article.title || 'Tally Blog';
    const description =
      article.seo_description ||
      article.excerpt ||
      (article.content ? String(article.content).replace(/<[^>]+>/g, '').slice(0, 155) : 'Read this guide on the Tally blog.');
    const image = article.cover_image || article.image || OG_IMAGE;

    document.title = title;
    upsertCanonical(url);
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[property="og:image"]', image);
    setMeta('meta[property="og:type"]', 'article');
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:image"]', image);

    setRouteJsonLd([
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.title || title,
        description,
        image,
        url,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        datePublished: article.published_at || article.created_at || undefined,
        dateModified: article.updated_at || article.published_at || article.created_at || undefined,
        author: article.author ? { '@type': 'Person', name: article.author } : ORG,
        publisher: { ...ORG, logo: { '@type': 'ImageObject', url: `${SITE}/logo.png` } },
      },
      breadcrumb([
        { name: 'Home', url: `${SITE}/` },
        { name: 'Articles', url: `${SITE}/articles` },
        { name: article.title || title, url },
      ]),
    ]);

    return () => {
      // Restore OG type to website when leaving the article
      setMeta('meta[property="og:type"]', 'website');
    };
  }, [article]);
};

/**
 * Component to set SEO metadata for a page.
 */
export const SEOHead = ({ path }) => {
  useSEO(path);
  return null;
};

export default SEOHead;
